import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Country codes data
const COUNTRY_CODES = [
  { code: '+91', country: 'India', flag: '🇮🇳', minLength: 10, maxLength: 10 },
  { code: '+1', country: 'USA/Canada', flag: '🇺🇸', minLength: 10, maxLength: 10 },
  { code: '+44', country: 'UK', flag: '🇬🇧', minLength: 10, maxLength: 11 },
  { code: '+61', country: 'Australia', flag: '🇦🇺', minLength: 9, maxLength: 9 },
  { code: '+971', country: 'UAE', flag: '🇦🇪', minLength: 9, maxLength: 9 },
  { code: '+65', country: 'Singapore', flag: '🇸🇬', minLength: 8, maxLength: 8 },
  { code: '+60', country: 'Malaysia', flag: '🇲🇾', minLength: 9, maxLength: 10 },
];

// Bhoj & Daan offerings data
const BHOJ_DAAN_OFFERINGS = [
  {
    id: 'aloo_sabzi_poori',
    name: 'Aloo Sabzi Poori',
    icon: '🍛',
    description: 'Serve wholesome meals of Aloo Sabzi with freshly made Pooris to those in need. This sacred act of Anna Daan (food donation) creates powerful positive karma and brings blessings to your family.',
    benefits: ['Removes Pitru Dosha', 'Brings prosperity', 'Earns divine blessings', 'Creates lasting good karma'],
    packages: [
      { id: 'asp_250', name: '250 People', people: 250, price: 12000 },
      { id: 'asp_500', name: '500 People', people: 500, price: 19000 },
      { id: 'asp_1000', name: '1000 People', people: 1000, price: 37000 },
    ]
  },
  {
    id: 'aloo_sabzi_poori_halwa',
    name: 'Aloo Sabzi Poori + Halwa',
    icon: '🍲',
    description: 'Complete the sacred meal with sweet Halwa alongside Aloo Sabzi and Pooris. The sweetness of Halwa symbolizes spreading joy and satisfaction to those you serve.',
    benefits: ['Double the blessings', 'Fulfills food offerings completely', 'Spreads joy to recipients', 'Maximum karmic merit'],
    packages: [
      { id: 'asph_250', name: '250 People', people: 250, price: 16000 },
      { id: 'asph_500', name: '500 People', people: 500, price: 31000 },
      { id: 'asph_1000', name: '1000 People', people: 1000, price: 59000 },
    ]
  },
  {
    id: 'stationary_school',
    name: 'Distribute Stationary to School Kids',
    icon: '📚',
    description: 'Gift the power of education to underprivileged children. Each kit includes Pencil, Eraser, Sharpener, Gel Pen, and Crayon Color Set - tools that can transform their future.',
    benefits: ['Supports child education', 'Creates Vidya Daan merit', 'Shapes bright futures', 'Blesses your own children'],
    packages: [
      { id: 'stat_200', name: '200 Kids', people: 200, price: 15000 },
    ]
  },
  {
    id: 'kurta_saree_oldage',
    name: 'Kurta Pajama + Saree to Old Age Home',
    icon: '👘',
    description: 'Bring dignity and warmth to our elders with traditional Kurta Pajama for men and beautiful Sarees for women. Vastra Daan to the elderly earns immense blessings.',
    benefits: ['Honors our elders', 'Vastra Daan merit', 'Brings family harmony', 'Removes obstacles in life'],
    packages: [
      { id: 'vastra_40', name: '20 Male + 20 Female', people: 40, price: 75000 },
    ]
  },
  {
    id: 'blind_school',
    name: 'Daily Usage Products in Blind School',
    icon: '👁️',
    description: 'Support visually impaired students with essential daily items they need. Your compassion lights up their world and creates profound spiritual merit.',
    benefits: ['Supports the differently-abled', 'Creates powerful karma', 'Removes eye-related doshas', 'Earns divine grace'],
    packages: [
      { id: 'blind_100', name: '100 Students', people: 100, price: 20000 },
    ]
  },
  {
    id: 'leprosy_ashram',
    name: 'Daily Use Products to Kusht (Leprosy) Ashram',
    icon: '🙏',
    description: 'Serve those affected by leprosy with dignity and compassion. This selfless seva removes deep-rooted karmic blocks and brings immense spiritual purification.',
    benefits: ['Highest form of seva', 'Removes chronic health karma', 'Purifies past life debts', 'Earns Moksha merit'],
    packages: [
      { id: 'kusht_100', name: '100 People', people: 100, price: 12000 },
    ]
  },
  {
    id: 'hospital_medicines',
    name: 'Generic Medicines to Hospital',
    icon: '💊',
    description: 'Provide life-saving medicines to those who cannot afford treatment. Aushadhi Daan (medicine donation) is considered one of the most meritorious acts in Dharma.',
    benefits: ['Saves lives directly', 'Aushadhi Daan merit', 'Removes health obstacles', 'Creates healing karma'],
    packages: [
      { id: 'med_100', name: '100 People', people: 100, price: 10000 },
    ]
  },
];

const BhojDaanPage = () => {
  const [selectedOfferings, setSelectedOfferings] = useState({});
  const [formData, setFormData] = useState({ name: '', phone: '', email: '', countryCode: '+91' });
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [orderId, setOrderId] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [expandedOffering, setExpandedOffering] = useState(null);
  
  const formRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const getSelectedCountry = () => COUNTRY_CODES.find(c => c.code === formData.countryCode) || COUNTRY_CODES[0];
  
  const isPhoneValid = () => {
    const country = getSelectedCountry();
    return formData.phone.length >= country.minLength && formData.phone.length <= country.maxLength;
  };

  // Success Screen
  if (bookingSuccess) {
    return (
      <div className="legal-page" data-testid="bhoj-daan-success">
        <div className="legal-container" style={{ textAlign: 'center', paddingTop: '60px' }}>
          <div style={{ fontSize: '4rem', marginBottom: '24px' }}>🎉</div>
          <h1 className="legal-title" style={{ color: '#4CAF50' }}>Donation Successful!</h1>
          
          <div style={{ 
            background: 'rgba(76, 175, 80, 0.1)', 
            border: '1px solid #4CAF50', 
            borderRadius: '12px', 
            padding: '32px', 
            margin: '32px auto',
            maxWidth: '500px'
          }}>
            <p style={{ color: 'var(--warm-white)', fontSize: '1.1rem', marginBottom: '16px' }}>
              Thank you for your generous donation!
            </p>
            <div style={{ 
              background: 'rgba(212, 168, 67, 0.1)', 
              borderRadius: '8px', 
              padding: '16px', 
              marginBottom: '16px' 
            }}>
              <p style={{ color: 'var(--gold)', fontWeight: '600', marginBottom: '8px' }}>Order ID</p>
              <p style={{ color: 'var(--warm-white)', fontSize: '1.2rem', fontFamily: 'monospace' }}>{orderId}</p>
            </div>
            <div style={{ 
              background: 'rgba(212, 168, 67, 0.1)', 
              borderRadius: '8px', 
              padding: '16px' 
            }}>
              <p style={{ color: 'var(--gold)', fontWeight: '600', marginBottom: '8px' }}>Customer ID</p>
              <p style={{ color: 'var(--warm-white)', fontSize: '1.2rem', fontFamily: 'monospace' }}>{customerId}</p>
            </div>
          </div>

          <div style={{ 
            background: 'rgba(212, 168, 67, 0.08)', 
            borderRadius: '12px', 
            padding: '24px', 
            margin: '24px auto',
            maxWidth: '600px'
          }}>
            <p style={{ color: 'var(--subtle-gold)', fontSize: '1rem', lineHeight: '1.8' }}>
              Within a week, this noble activity will be performed and we will share the 
              <strong style={{ color: 'var(--gold)' }}> live video sharing link</strong> with you via WhatsApp.
            </p>
            <p style={{ color: 'var(--light-cream)', marginTop: '16px', fontSize: '0.95rem' }}>
              May your generosity bring abundant blessings to you and your family.
            </p>
          </div>

          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', marginTop: '32px', flexWrap: 'wrap' }}>
            <Link to="/my-consultation" className="btn btn-ember">
              View My Orders
            </Link>
            <Link to="/" className="btn btn-outline">
              Back to Home
            </Link>
          </div>

          <div style={{ marginTop: '40px', display: 'flex', gap: '16px', justifyContent: 'center' }}>
            <a href="https://www.instagram.com/amatyalive/" target="_blank" rel="noopener noreferrer" style={{color:'#D4A843'}}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
            </a>
            <a href="https://x.com/amatyalive" target="_blank" rel="noopener noreferrer" style={{color:'#D4A843'}}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
            </a>
            <a href="https://www.youtube.com/@amatyalive" target="_blank" rel="noopener noreferrer" style={{color:'#D4A843'}}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bhoj-daan-page" data-testid="bhoj-daan-page">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Bhoj & Daan - Sacred Charity Services | Amatya</title>
        <meta name="description" content="Perform sacred Bhoj & Daan (भोज एवं दान) charity services. Feed the needy, donate to temples, and earn divine blessings through Anna Daan, Vastra Daan, and more." />
        <meta name="keywords" content="Bhoj Daan, Anna Daan, food donation, charity, Vastra Daan, temple donation, sacred giving, positive karma" />
        <link rel="canonical" href="https://www.amatya.co.in/bhoj-daan" />
        <meta property="og:title" content="Bhoj & Daan - Sacred Charity Services | Amatya" />
        <meta property="og:description" content="Transform lives through sacred Bhoj & Daan charity services. Every act of giving creates positive karma." />
        <meta property="og:url" content="https://www.amatya.co.in/bhoj-daan" />
      </Helmet>
      
      {/* Hero Section */}
      <section className="bhoj-daan-hero">
        <div className="bhoj-daan-hero-content">
          <span className="hero-label">SACRED CHARITY</span>
          <h1 className="hero-title">Bhoj & Daan</h1>
          <p className="hero-subtitle">भोज एवं दान</p>
          <p className="hero-description">
            Transform lives through the timeless practice of Daan (charity). 
            Every act of giving creates ripples of positive karma that return 
            to bless you and your family manifold.
          </p>
          <button onClick={() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })} className="btn btn-ember">
            Start Your Seva <span className="btn-arr">→</span>
          </button>
        </div>
      </section>

      {/* Why Bhoj & Daan Section */}
      <section className="bhoj-daan-why">
        <div className="container">
          <h2 className="section-title">Why Bhoj & Daan?</h2>
          <p className="section-subtitle">
            In Sanatana Dharma, Daan is not just charity—it's a sacred practice that purifies the soul and creates lasting positive karma.
          </p>
          
          <div className="why-grid">
            <div className="why-card">
              <div className="why-icon">🙏</div>
              <h3>Pitru Rin Mukti</h3>
              <p>Release ancestral debts and bring peace to your forefathers through acts of compassion.</p>
            </div>
            <div className="why-card">
              <div className="why-icon">⭐</div>
              <h3>Positive Karma</h3>
              <p>Every rupee spent in seva multiplies into blessings that protect you and your family.</p>
            </div>
            <div className="why-card">
              <div className="why-icon">🎥</div>
              <h3>Documented Proof</h3>
              <p>Receive live video of your donation being distributed—witness your impact firsthand.</p>
            </div>
            <div className="why-card">
              <div className="why-icon">✅</div>
              <h3>Verified Recipients</h3>
              <p>We partner with verified ashrams, schools, and hospitals to ensure your donation reaches those truly in need.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Offerings Section */}
      <section className="bhoj-daan-offerings" ref={formRef}>
        <div className="container">
          <h2 className="section-title">Available Seva Offerings</h2>
          <p className="section-subtitle">Explore our seva offerings — tap any card to see details, then reach us on WhatsApp to proceed.</p>

          <div className="offerings-grid">
            {BHOJ_DAAN_OFFERINGS.map((offering) => (
              <div 
                key={offering.id} 
                className={`offering-card ${expandedOffering === offering.id ? 'expanded' : ''}`}
                data-testid={`offering-${offering.id}`}
              >
                <div 
                  className="offering-header"
                  onClick={() => setExpandedOffering(expandedOffering === offering.id ? null : offering.id)}
                >
                  <div className="offering-icon">{offering.icon}</div>
                  <h3 className="offering-name">{offering.name}</h3>
                  <span className="expand-arrow">{expandedOffering === offering.id ? '▲' : '▼'}</span>
                </div>
                
                {expandedOffering === offering.id && (
                  <div className="offering-details">
                    <p className="offering-description">{offering.description}</p>
                    <div className="offering-benefits">
                      <h4>Benefits:</h4>
                      <ul>
                        {offering.benefits.map((benefit, idx) => (
                          <li key={idx}>{benefit}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                <div className="offering-packages">
                  <div className="package-label">Available packages:</div>
                  {offering.packages.map((pkg) => (
                    <div
                      key={pkg.id}
                      className="package-radio-btn"
                      data-testid={`package-${pkg.id}`}
                      style={{ cursor: 'default' }}
                    >
                      <span className="package-info">
                        <span className="package-name">{pkg.name}</span>
                        <span className="package-price">₹{pkg.price.toLocaleString()}</span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WhatsApp CTA Section */}
      <section className="bhoj-daan-form" ref={formRef} data-testid="bhoj-daan-whatsapp-cta">
        <div className="container">
          <div className="form-card" style={{ textAlign: 'center' }}>
            <h2 className="form-title">Ready to Offer Your Seva?</h2>
            <p style={{ color: 'var(--subtle-gold, #D4A843)', fontSize: '1rem', lineHeight: 1.7, maxWidth: 560, margin: '0 auto 28px' }}>
              Let us know which seva calls to your heart. We will guide you through the offering and share live video documentation once it is performed in your name.
            </p>

            <a
              href="https://wa.me/917633997555?text=Hi%20Amatya%2C%20I%20would%20like%20to%20offer%20a%20charity%20(Bhoj%20%26%20Daan).%20Please%20share%20the%20next%20steps."
              target="_blank"
              rel="noopener noreferrer"
              data-testid="whatsapp-charity-btn"
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '12px',
                background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
                color: '#fff', textDecoration: 'none',
                padding: '18px 32px', fontFamily: 'Cinzel, serif', fontSize: '0.95rem',
                fontWeight: 600, letterSpacing: '0.04em',
                boxShadow: '0 6px 24px rgba(18, 140, 126, 0.3)',
                borderRadius: '6px', textAlign: 'center', lineHeight: 1.35,
                maxWidth: '100%'
              }}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.464 3.488" /></svg>
              Offer a Charity (भोज एवं दान) Now!!
            </a>

            <div style={{ marginTop: 20, fontFamily: 'Lato, sans-serif', fontSize: '0.9rem', color: 'var(--light-cream, #E8DCC8)' }}>
              <span>📱</span> &nbsp; +91 7633 997 555
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="bhoj-daan-trust">
        <div className="container">
          <h2 className="section-title">Our Promise</h2>
          <div className="trust-grid">
            <div className="trust-item">
              <span className="trust-icon">📹</span>
              <p>Live video documentation of your donation</p>
            </div>
            <div className="trust-item">
              <span className="trust-icon">📍</span>
              <p>Verified locations across India</p>
            </div>
            <div className="trust-item">
              <span className="trust-icon">🔒</span>
              <p>Secure Razorpay payments</p>
            </div>
            <div className="trust-item">
              <span className="trust-icon">📞</span>
              <p>WhatsApp support throughout</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BhojDaanPage;
